import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Dirt Trail",
  "Mulch Trail",
  "Rock / Retaining Work",
  "Maintenance & Restoration",
  "Not sure yet",
];

const inputCls =
  "w-full bg-transparent border-b border-[#D1CFC7] rounded-none px-0 py-3 focus:outline-none focus:border-[#2D4A3E] text-[#1E352F] placeholder-[#9A9A92] transition-colors";

export default function Quote() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    location: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Thanks! We'll be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="quote-page" className="pt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Left image / info */}
          <div className="lg:col-span-5">
            <span className="text-xs tracking-[0.25em] uppercase font-semibold text-[#A95C42]">
              Let's build something
            </span>
            <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tight font-medium text-[#1E352F] mt-2">
              Request a Quote
            </h1>
            <p className="text-base text-[#4A5D54] mt-3 leading-relaxed">
              Tell us about your property and what you have in mind. We'll get
              back to you to talk through the route, surface, and a fair estimate.
            </p>
            <div className="relative overflow-hidden rounded-sm border border-[#D1CFC7] mt-6 aspect-[4/3]">
              <img
                src={IMAGES.showcaseLake}
                alt="Montana forest and mountains by a lake"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="mt-6 text-sm text-[#4A5D54] space-y-1">
              <p className="font-medium text-[#1E352F]">Jonathan Oftedahl</p>
              <a href="tel:+14064713203" className="block text-[#2D4A3E] hover:underline">
                (406) 471-3203
              </a>
              <a
                href="mailto:Jonathan@flatheadtrailsystems.com"
                className="block text-[#2D4A3E] hover:underline"
              >
                Jonathan@flatheadtrailsystems.com
              </a>
              <p>P.O. Box 8842, Kalispell, MT 59904</p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                data-testid="quote-success"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-sm border border-[#D1CFC7] bg-[#FFFFFF] p-10 flex flex-col items-start"
              >
                <CheckCircle2 className="h-12 w-12 text-[#2D4A3E]" strokeWidth={1.5} />
                <h2 className="font-serif-display text-3xl text-[#1E352F] mt-4">
                  Request received
                </h2>
                <p className="text-[#4A5D54] mt-2 leading-relaxed">
                  Thank you, {form.name.split(" ")[0] || "friend"}. We'll reach out
                  to you at {form.email} soon to talk through your trail.
                </p>
                <button
                  data-testid="quote-reset-button"
                  onClick={() => {
                    setDone(false);
                    setForm({ name: "", email: "", phone: "", project_type: "", location: "", message: "" });
                  }}
                  className="mt-6 border border-[#2D4A3E] text-[#2D4A3E] px-6 py-3 rounded-sm font-medium hover:bg-[#EAE7E0] transition-colors"
                >
                  Send another request
                </button>
              </motion.div>
            ) : (
              <form
                data-testid="quote-form"
                onSubmit={submit}
                className="rounded-sm border border-[#D1CFC7] bg-[#FFFFFF] p-7 lg:p-10 grid grid-cols-1 sm:grid-cols-2 gap-6"
              >
                <div className="sm:col-span-1">
                  <label className="text-xs tracking-[0.15em] uppercase text-[#4A5D54]">Name *</label>
                  <input data-testid="quote-name-input" value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" />
                </div>
                <div className="sm:col-span-1">
                  <label className="text-xs tracking-[0.15em] uppercase text-[#4A5D54]">Email *</label>
                  <input data-testid="quote-email-input" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@email.com" />
                </div>
                <div className="sm:col-span-1">
                  <label className="text-xs tracking-[0.15em] uppercase text-[#4A5D54]">Phone</label>
                  <input data-testid="quote-phone-input" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="(406) 555-0100" />
                </div>
                <div className="sm:col-span-1">
                  <label className="text-xs tracking-[0.15em] uppercase text-[#4A5D54]">Location</label>
                  <input data-testid="quote-location-input" value={form.location} onChange={update("location")} className={inputCls} placeholder="Town / property area" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs tracking-[0.15em] uppercase text-[#4A5D54]">Project type</label>
                  <select data-testid="quote-type-select" value={form.project_type} onChange={update("project_type")} className={`${inputCls} appearance-none`}>
                    <option value="">Select one…</option>
                    {PROJECT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs tracking-[0.15em] uppercase text-[#4A5D54]">Tell us about your trail *</label>
                  <textarea data-testid="quote-message-input" value={form.message} onChange={update("message")} rows={4} className={`${inputCls} resize-none`} placeholder="Length, terrain, surface you'd like, timing, anything else…" />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    data-testid="quote-submit-button"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 bg-[#2D4A3E] text-[#F8F7F4] px-8 py-3.5 rounded-sm font-medium hover:bg-[#1E352F] transition-colors duration-300 disabled:opacity-60"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {submitting ? "Sending…" : "Send Request"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
